// The words on Today (AdminToday.dc.html), worked out from what the database gives, at read time:
// every kind of job hub.today_jobs gives as its card (the board's 8, the Price map's mismatch words,
// S05's causes, stage4-for-adam 17's fast finish, and the rest, which no board draws, in the stage 5
// batch for Adam), Live today, Coming up, New this week, Going quiet, Access ending and the
// announcements. Pure: it imports only the date helpers, so tests/e2e/admin-today.spec.js checks every
// line on its own.
//
// Adam's writing rules hold throughout: British English, numerals, no exclamation marks or dashes,
// straight quotes, contractions, and a person is named, never "he", "she" or "they" (the hub doesn't
// know anyone's pronouns). No line claims an email the hub doesn't send: there's no session reminder,
// new-recording email or any other notification email (Adam, 5 Oct, stage 6), and nothing comes from
// Zoom, so the board's "New in Zoom" job, "Reminders went out" and Zoom's registered counts are gone.
import { realFirstName } from '../../lib/names.js'
import { time, dayMonth, shortDate, longDate, clock, uk, dateBlock } from '../../lib/format.js'
import { TERM_CHIP, termJobDetail } from '../mastermind/words.js'
import {
  MISMATCH_WORDS, doneStamp, paidText, paidActions, mapLink, createNeedsEmail, noPaymentText, reasonDone, revokeDone,
} from '../mismatches.js'
import { gbp as poundsWord, STATUS_WORDS as REF_STATUS, statusInSentence } from '../referrals/words.js'
import { adminPath } from '../shell/routes.js'

const DAY_MS = 86400000

// "Sam" for Sam Carter; a first name, else the full name, else the email, else "this person"
// (the database's "there", a name that gave none, isn't a first name here: lib/names.js)
export const firstOf = j => realFirstName(j.person_first_name) || realFirstName(j.first_name) || j.person_full_name || j.full_name || j.person_email || j.email || 'this person'
export const fullOf = j => j.person_full_name || j.full_name || j.person_email || j.email || 'Someone'
// Sam's, James's (Adam's own rule for possessives: just 's)
export const possessive = name => `${name}'s`

// The masterclass is "Masterclass" on the admin boards (its full name carries the 1 word the hub
// keeps for its name alone); every other product goes by its name. It's the video at /masterclass: by
// its slug, or the type where no slug came with the row. Another masterclass made in admin (the
// product-types plan, 13 Oct) goes by its own name.
const slugOf = p => p?.product_slug || p?.slug || null
const isMasterclass = p => slugOf(p) === 'masterclass' || (!slugOf(p) && (p?.product_type || p?.type) === 'masterclass')
const isMasterclassType = p => (p?.product_type || p?.type) === 'masterclass'
// a 1-2-1 space's own page: its product's path (each client space product's spaces stay apart), the
// 1-2-1 Mentorship's when a row carries none
const spaceLink = (path, spaceId) => `${path || '/mentorship'}?space=${spaceId}`
export function productTitle(p) {
  if (!p) return ''
  if (isMasterclass(p)) return 'Masterclass'
  return p.product_name || p.name || ''
}
// A read row that names its product only by id and name (New this week, Access ending) with the
// product's type and slug from the catalogue, so the masterclass is named as the boards name it
export function typed(row, products = []) {
  const p = products.find(x => x.id === row.product_id)
  return p ? { ...row, product_type: row.product_type || p.type, product_slug: row.product_slug || p.slug } : row
}
// in a sentence: "the masterclass", "The Funding Engine"
export function productIn(p) {
  if (!p) return 'the product'
  if (isMasterclass(p)) return 'the masterclass'
  return p.product_name || p.name || 'the product'
}

// 30 Sep, or 30 Sep 2027 when it isn't this year
export function dayShort(t, nowMs = Date.now()) {
  if (!t) return ''
  const a = uk(t), n = uk(nowMs)
  return a.y === n.y ? dayMonth(t) : `${dayMonth(t)} ${a.y}`
}
// Wed 7 Oct, or Wed 7 Oct 2027
export function dayWithName(t, nowMs = Date.now()) {
  if (!t) return ''
  const a = uk(t), n = uk(nowMs)
  return a.y === n.y ? shortDate(t) : `${shortDate(t)} ${a.y}`
}
const daysBetween = (nowMs, t) => uk(t).day - uk(nowMs).day

// "today", "tomorrow", "on Thu 1 Oct"
export function relativeDay(t, nowMs = Date.now()) {
  const d = daysBetween(nowMs, t)
  if (d === 0) return 'today'
  if (d === 1) return 'tomorrow'
  if (d === -1) return 'yesterday'
  return `on ${dayWithName(t, nowMs)}`
}

// Week 2 at 45:33: the question's week or module as the database names it (question_module_label,
// since the stage 5 integration), else read from where it was asked (/funding-engine/week-2?t=45m33s)
export function askedWhere(job) {
  const path = String(job.question_context_path || '').split('?')[0]
  const last = path.split('/').filter(Boolean).pop() || ''
  const m = last.match(/^(week|module|session)-(\d+)$/)
  const place = job.question_module_label || (m ? `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}` : '')
  const at = job.question_at_seconds != null ? clock(job.question_at_seconds) : ''
  if (place && at) return `${place} at ${at}`
  return place || (at ? `at ${at}` : '')
}

// The chip on each kind of job: its words, and whether it's the board's black chip (money, a deadline,
// or a finisher, as the board's Payment, Finished and Mismatch are) or its grey 1
const CHIPS = {
  payment_failed: ['Payment', true],
  crosses_7_day_line: ['Payment', true],
  balance_past_due: ['Balance', true],
  refund: ['Refund', true],
  dispute: ['Dispute', true],
  paid_no_access: ['Mismatch', true],
  access_no_payment: ['Mismatch', true],
  access_cancelled_subscription: ['Cancelled', true],
  plan_payments_complete: ['Plan paid', true],
  bought_over_grant: ['Bought', true],
  paid_twice: ['Paid twice', true],
  data_request: ['Data request', true],
  question: ['Question', false],
  client_session_soon: ['1-2-1', false],
  recording_missing: ['Recording', false],
  register_missing: ['Register', false],
  course_finished: ['Finished', true],
  fast_finish: ['Fast finish', true],
  login_email_mismatch: ['Login', false],
  email_send_failed: ['Email', false],
  // the Accelerator's 12-month term (the morning check, the mastermind build): its final month, and past it
  term_final_month: [TERM_CHIP, false],
  term_past_end: [TERM_CHIP, false],
  // Profit On Your Terms (POYT.dc.html "Book your 30 minutes"): a member asked for their deal surgery
  deal_surgery: ['Deal surgery', false], // undrawn
  // a Stripe buyer's intake to check: 1 that had started, or none (migration 20261010233000)
  intake_check: ['Intake', false], // undrawn
  // referrals (decision 55, migration 79): an introduction to look at, owed, or that couldn't be recorded
  introduction: ['Referral', true], // undrawn
  // a mastermind's reviews after a session, and a member still to set up (the Accelerator critique's admin 1, Adam's
  // yes of 20 Oct): worked out when Today is read (mastermindJobs.js), never stored (undrawn)
  mm_review: ['Review', false],
  mm_setup: ['Set up', false],
}

// The order Needs you lists them in, as the board does: payments first, then the member waiting on an
// answer, then the day's sessions, recordings and registers, then finishers and the money to check
const ORDER = [
  ['payment_failed', 'crosses_7_day_line', 'balance_past_due'],
  ['refund', 'dispute'],
  ['data_request'],
  ['question'],
  ['deal_surgery'],
  ['client_session_soon'],
  ['recording_missing'],
  ['register_missing'],
  ['mm_review'],
  ['mm_setup'],
  ['course_finished', 'fast_finish'],
  ['term_final_month', 'term_past_end'],
  ['paid_no_access', 'access_no_payment'],
  ['intake_check'],
  ['introduction'],
  ['access_cancelled_subscription', 'plan_payments_complete', 'bought_over_grant', 'paid_twice'],
  ['login_email_mismatch', 'email_send_failed'],
]
const RANK = Object.fromEntries(ORDER.flatMap((kinds, i) => kinds.map(k => [k, i])))
// A job's kind for its words: the payment group by the access row's state, else the latest kind
export function jobKind(job) {
  const kinds = job.kinds || [job.kind]
  if (job.job_key?.startsWith('access:')) {
    if (job.access_status === 'suspended') return 'crosses_7_day_line'
    if (job.access_status === 'secured' && kinds.includes('balance_past_due')) return 'balance_past_due'
    if (kinds.includes('payment_failed')) return 'payment_failed'
  }
  return job.kind
}
// Open jobs first, by kind as the board orders them, newest first within a kind
const byOrder = (a, b) => (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1)
  || (RANK[jobKind(a)] ?? 99) - (RANK[jobKind(b)] ?? 99)
  || (Date.parse(b.raised_at) || 0) - (Date.parse(a.raised_at) || 0)
  || String(a.job_key).localeCompare(String(b.job_key))
export function sortJobs(jobs) {
  return [...jobs].sort(byOrder)
}
// A reload keeps the rows where they were (a ticked job stays in its place, faded, as on the board);
// a job new since the last load goes in by the order above, before the first row it comes ahead of
// (so a mastermind's jobs, read after the rest, go in after the registers: mastermindJobs.js)
export function keepPlaces(before, after) {
  if (!before?.length) return sortJobs(after)
  const known = new Map(after.map(j => [j.job_key, j]))
  const out = before.filter(j => known.has(j.job_key)).map(j => known.get(j.job_key))
  for (const job of sortJobs(after.filter(j => !before.some(b => b.job_key === j.job_key)))) {
    const at = out.findIndex(row => byOrder(job, row) < 0)
    out.splice(at < 0 ? out.length : at, 0, job)
  }
  return out
}

// The amounts a job and a New this week row need from stripe-prices (owner and admin only): invoice,
// checkout, charge and price ids, at most 50 in all
export function amountIds(jobs = [], fresh = []) {
  const ids = { invoice_ids: new Set(), checkout_session_ids: new Set(), charge_ids: new Set(), price_ids: new Set() }
  const add = (set, v) => { if (typeof v === 'string' && v) set.add(v) }
  for (const j of jobs) {
    // a referral card shows no amount from Stripe: what's owed is the hub's own (hub.staff_introductions)
    if (j.kind === 'introduction') continue
    const d = j.detail || {}
    add(ids.invoice_ids, d.invoice_id)
    add(ids.checkout_session_ids, d.checkout_session_id)
    add(ids.charge_ids, d.charge_id)
    add(ids.price_ids, d.price_id)
    for (const p of Object.keys(d.prices || {})) add(ids.price_ids, p)
  }
  for (const f of fresh) {
    if (f.kind !== 'access' || f.status === 'secured') continue
    add(ids.checkout_session_ids, f.checkout_session_id)
    add(ids.price_ids, f.price_id)
  }
  const out = {}
  let room = 50
  for (const [k, set] of Object.entries(ids)) {
    const list = [...set].slice(0, room)
    room -= list.length
    if (list.length) out[k] = list
  }
  return out
}

// An amount as the boards write it ("£249+vat"), from stripe-prices' answer, or ''
export function amountOf(amounts, { invoice, checkout, charge, price } = {}) {
  if (!amounts) return ''
  return amounts.invoices?.[invoice]?.display || amounts.charges?.[charge]?.display
    || amounts.checkouts?.[checkout]?.display || amounts.prices?.[price]?.display || ''
}

const TEMPLATES = {
  login_code: 'A login code',
  new_email_code: 'A code to confirm a new email',
  access: 'The access email',
  resend_login: 'A login email',
  card_link: 'A card link',
  certificate: 'A certificate email',
  question_reply: 'A reply to a question',
  data_export: 'The data export email',
  login_changed: 'A login email change notice',
}

const LOGIN_REASONS = {
  linked_login_email_differs: n => `${possessive(n)} login has a different email from the hub's, so no login code went.`,
  several_logins_for_email: () => 'More than 1 login has this email, so no login code went.',
  login_linked_to_another_person: n => `The login with this email belongs to another record in the hub, so no login code went. If it's the same person, merge the 2 records on ${possessive(n)} page.`,
  linked_to_another_login_meanwhile: n => `${possessive(n)} hub account was linked to another login while a code was asked for, so no login code went.`,
  email_change_half_done: n => `${possessive(n)} new email went onto the login but not into the hub.`,
  move_email_half_done: n => `Moving ${possessive(n)} login email went onto the login but not into the hub.`,
}

// The reasons whose fix isn't a new login email: the card ends with its own fix instead
const LOGIN_OWN_FIX = new Set(['login_linked_to_another_person'])

const OA = ['owner', 'admin']
const ALL = ['owner', 'admin', 'support', 'host']
const OWNER = ['owner']

// A recording job's key in ctx.bells: its product and the recording, if it has 1 yet
export const bellKey = job => `${job.product_id || ''}|${job.recording_id || ''}`

// The recording jobs whose bell to ask about (hub.bell_will_ring), each once: [{ key, product, item }]
export function bellAsks(jobs = []) {
  const out = new Map()
  for (const j of jobs) {
    if (jobKind(j) !== 'recording_missing' || !j.product_id) continue
    out.set(bellKey(j), { key: bellKey(j), product: j.product_id, item: j.recording_id || null })
  }
  return [...out.values()]
}

// The card for 1 job: { chip, ink, title, detail, note, link: { label, to }, actions: [{ op, label,
// kind, roles }] }. ctx: { role, nowMs, amounts, products, bells } (products: the catalogue, for an
// offer's name; bells: { bellKey: true } for a recording the database will put in the bell once it's
// switched on). The screen keeps only the actions the role may take.
export function jobCard(job, ctx = {}) {
  const nowMs = ctx.nowMs ?? Date.now()
  const kind = jobKind(job)
  const [chip, ink] = CHIPS[kind] || ['Job', false]
  const first = firstOf(job)
  const full = fullOf(job)
  const d = job.detail || {}
  const person = job.person_id ? { label: `Open ${first}`, to: `/admin/people/${job.person_id}` } : null
  // a mastermind's member opens in Masterminds for owner and admin (memberHref), where the payment buttons are too
  const member = person && { ...person, to: memberHref(job, ctx.products, ctx.role) }
  const amount = amountOf(ctx.amounts, { invoice: d.invoice_id, checkout: d.checkout_session_id, charge: d.charge_id, price: d.price_id || Object.keys(d.prices || {})[0] })
  const on = t => dayShort(t, nowMs)
  const card = { chip, ink, kind, title: '', detail: '', note: '', link: null, actions: [] }
  const act = (op, label, roles = OA, kind2 = 'ink') => card.actions.push({ op, label, roles, kind: kind2 })
  const nameProduct = `${full}, ${productTitle(job)}`

  // A Stripe event that still failed a day on, whatever kind it raised, in words rather than Stripe's
  // own code (Adam, 5 Oct, question 17)
  if (d.reason === 'processing_failed' && kind !== 'paid_no_access') {
    const what = kind === 'refund' || d.event_type === 'charge.refunded' ? 'A refund from Stripe' : 'An event from Stripe'
    const tries = Number(d.attempts) > 0 ? `${d.attempts} ${Number(d.attempts) === 1 ? 'try' : 'tries'}` : 'several tries'
    card.title = 'A payment from Stripe couldn\'t be processed'
    card.detail = `${what} on ${on(job.raised_at)} couldn't be applied after ${tries}, so nothing changed. Look at it in Stripe.`
    card.link = person
    return card
  }

  switch (kind) {
    // admin's words keep "Suspended" for the 7-day unpaid rule and "Paused" for a pause Adam or Stripe set (the
    // Accelerator critique's admin 5, 20 Oct); members' own words don't change
    case 'payment_failed': {
      if (job.access_status === 'secured' || d.place) {
        card.title = `${nameProduct}, place secured`
        card.detail = `${amount ? `A balance payment of ${amount}` : 'A balance payment'} failed on ${on(job.access_payment_failed_at || job.raised_at)}. The place stays secured, and nothing opens until the balance is paid.`
        card.link = person
      } else {
        card.title = nameProduct
        const suspends = job.access_status === 'overdue' && (job.access_suspend_on || d.suspend_on)
          ? ` Suspended on ${on(job.access_suspend_on || d.suspend_on)} if it isn't paid.` : ''
        const banner = job.access_status === 'overdue' ? ` The banner is showing on ${possessive(first)} library.` : ''
        card.detail = `${amount || 'The payment'} failed on ${on(job.access_payment_failed_at || job.raised_at)}.${banner}${suspends}`
        card.link = member
      }
      if (job.has_customer) act('card_link', 'Send card link')
      break
    }
    case 'crosses_7_day_line': {
      card.title = nameProduct
      const when = job.access_suspend_on || d.suspend_on
      const suspended = when && daysBetween(nowMs, when) === 0 ? 'Suspended today.' : `Suspended on ${on(when || job.raised_at)}.`
      card.detail = `${suspended} ${amount || 'The payment'} has been unpaid since ${on(job.access_payment_failed_at || job.raised_at)}. It opens again the moment the payment goes through.`
      card.link = member
      if (job.has_customer) act('card_link', 'Send card link')
      break
    }
    case 'balance_past_due': {
      card.title = `${nameProduct}, place secured`
      const due = job.access_balance_due_at || d.balance_due_at
      card.detail = `The balance was due by ${time(due)} on ${on(due)} and isn't paid. Nothing has opened, and the place stays secured.`
      card.link = person
      if (job.access_id && job.access_status === 'secured') act('give_7_days', 'Give 7 more days')
      break
    }
    case 'refund':
    case 'dispute': {
      const open = ['active', 'overdue', 'suspended', 'paused', 'secured'].includes(job.access_status)
      if (!job.person_id) {
        card.title = kind === 'refund' ? 'A refund the hub can\'t place' : 'A dispute the hub can\'t place'
        // with no amount read (stripe-prices down, or no charge id) the line still has a subject (undrawn)
        card.detail = `${amount || 'A payment'} ${kind === 'refund' ? 'was refunded' : 'was disputed'} on ${on(job.raised_at)}, and the hub can't find who it was for. Look at it in Stripe.`
        break
      }
      card.title = nameProduct
      card.detail = kind === 'refund'
        ? `${amount ? `${amount} was` : 'The payment was'} refunded in full on ${on(job.raised_at)}. Nothing has changed on its own.`
        : `${possessive(first)} bank disputed ${amount || 'the payment'} on ${on(job.raised_at)}. Nothing has changed on its own. Answer it in Stripe.`
      if (open) {
        card.detail += ` End ${possessive(first)} access, or keep it${kind === 'refund' ? ' as goodwill' : ''}. Progress is kept either way.`
        act('end_access', `End ${possessive(first)} access`)
        act('keep_access', `Keep ${possessive(first)} access`, OA, 'line')
      } else if (job.access_status) {
        card.detail += ` ${possessive(first)} access has already ended.`
      }
      card.link = person
      break
    }
    // the Price map's same 2 jobs: the words, buttons and what each does are src/admin/mismatches.js's
    case 'paid_no_access': {
      card.title = MISMATCH_WORDS.kindPaid
      card.detail = paidText(job, amount, nowMs)
      // an unmapped price is mapped on the Price map; the rest have the person's page, where there's 1
      card.link = d.reason === 'unmapped_price' ? mapLink(ctx.role) : person
      for (const op of paidActions(job, ctx.role)) {
        if (op === 'apply') act('apply_payment', MISMATCH_WORDS.apply)
        else if (op === 'create') act(createNeedsEmail(job) ? 'create_member_email' : 'create_member', MISMATCH_WORDS.create)
        else if (op === 'match') act('match', MISMATCH_WORDS.match, OA, 'line')
        else if (op === 'handled') act('handled', MISMATCH_WORDS.handled, OA, 'line')
      }
      break
    }
    case 'access_no_payment': {
      card.title = MISMATCH_WORDS.kindNoPayment
      card.detail = noPaymentText(job)
      card.link = person
      if (job.access_id) {
        act('add_reason', MISMATCH_WORDS.addReason)
        act('revoke', MISMATCH_WORDS.revoke, OA, 'line')
      }
      break
    }
    case 'access_cancelled_subscription': {
      card.title = job.access_status === 'secured' ? `${nameProduct}, place secured` : nameProduct
      card.detail = job.access_status === 'secured'
        ? `The balance plan for ${possessive(first)} place was cancelled in Stripe on ${on(job.raised_at)}. The place stays secured and nothing opens.`
        : `The subscription was cancelled in Stripe on ${on(job.raised_at)}.`
      card.link = person
      break
    }
    case 'plan_payments_complete': {
      card.title = nameProduct
      card.detail = `${first} has made all ${d.payments_total || 'the'} payments. Access stays. End the subscription in Stripe if it's still running.`
      card.link = person
      break
    }
    case 'bought_over_grant': {
      card.title = nameProduct
      card.detail = `${first} bought ${productIn(job)}${amount ? ` for ${amount}` : ''} on ${on(job.raised_at)}, after you'd given it to ${first} by hand. Both stay on ${possessive(first)} page, so it may need a refund in Stripe.`
      card.link = person
      break
    }
    case 'paid_twice': {
      card.title = nameProduct
      const paid = `${first} paid${amount ? ` ${amount}` : ''} for ${productIn(job)} again on ${on(job.raised_at)}`
      card.detail = d.reason === 'place_already_secured'
        ? `${first} secured a place on ${productIn(job)} again on ${on(job.raised_at)}${amount ? `, paying ${amount}` : ''}. The first place stands, so it may need a refund in Stripe.`
        : d.reason === 'full_price_on_secured_place'
          ? `${first} paid the full price${amount ? ` of ${amount}` : ''} for ${productIn(job)} on ${on(job.raised_at)} with a place already secured, so the place's payment may need a refund in Stripe.`
          : `${paid}, with access already open. Nothing new opened, so it may need a refund in Stripe.`
      card.link = person
      break
    }
    case 'question': {
      const where = askedWhere(job)
      if (job.client_space_id) {
        card.title = `${full}, 1-2-1 space`
        // where it was asked carries the space's own product path; an older row's went to the Mentorship
        const to = job.question_context_path && job.question_context_path.includes('?space=')
          ? job.question_context_path : spaceLink(null, job.client_space_id)
        card.link = { label: `Open ${possessive(first)} space`, to }
      } else {
        card.title = [full, productTitle(job), where].filter(Boolean).join(', ')
        const at = job.question_at_seconds != null ? clock(job.question_at_seconds) : ''
        if (job.question_context_path) card.link = { label: at ? `Open at ${at}` : 'Open where it was asked', to: job.question_context_path }
      }
      card.detail = job.question_body || ''
      if (!job.question_replied_at) act('reply', 'Reply by email', ALL)
      break
    }
    case 'course_finished': {
      const o = job.offer || {}
      // any masterclass's offer detail; its title names it as productIn does
      if (isMasterclassType(job)) {
        card.title = `${full} watched ${productIn(job)} to the end`
        const offerName = (ctx.products || []).find(p => p.id === o.offer_product_id)?.name || 'the course'
        if (o.offer_product_id && o.owns_offer_product) {
          card.detail = `${first} already has ${offerName}, so there's no offer to follow up.`
        } else if (o.offer_reached_at) {
          card.detail = `${first} reached the ${offerName} offer on ${on(o.offer_reached_at)} and hasn't bought it. The hub sends nothing about finishing, so a 1-to-1 message from you is the next step.`
        } else {
          card.detail = `${first} watched to the end on ${on(o.finished_at || job.raised_at)}.`
        }
      } else {
        card.title = `${full} finished ${productIn(job)}`
        const c = job.certificate
        card.detail = c?.number
          ? (c.revoked_at
            ? `Finished on ${on(o.finished_at || job.raised_at)}. Certificate ${c.number} has been revoked.`
            : `Finished on ${on(o.finished_at || job.raised_at)}. Certificate ${c.number} was issued on ${on(c.issued_at)}.`)
          : `Finished on ${on(o.finished_at || job.raised_at)}.`
      }
      card.link = person
      act('messaged', 'Mark as messaged', ALL)
      break
    }
    case 'fast_finish': {
      card.title = `${full} finished ${productIn(job)} fast`
      const parts = []
      if (d.fast_recordings != null && d.recordings) parts.push(`${d.fast_recordings} of ${d.recordings} recordings marked watched faster than 2x.`)
      if (d.fast_run && d.run_seconds != null && d.watch_seconds != null) parts.push(`The whole run took ${spanWords(d.run_seconds)}, against ${spanWords(d.watch_seconds)} of recordings.`)
      const c = job.certificate
      const number = c?.number || d.number
      if (number) parts.push(c?.revoked_at ? `Certificate ${number} has been revoked.` : `Certificate ${number} was issued.`)
      card.detail = parts.join(' ')
      card.link = job.person_id ? { label: `Look at ${possessive(first)} progress`, to: `/admin/people/${job.person_id}` } : null
      if (number && !c?.revoked_at && (c?.certificate_id || d.certificate_id)) act('revoke_certificate', 'Revoke the certificate', OA, 'line')
      break
    }
    case 'recording_missing': {
      const label = job.session_module_label || job.recording_module_label || job.session_title
      card.title = [productTitle(job), label].filter(Boolean).join(', ')
      const starts = job.session_starts_at
      const ends = starts ? Date.parse(starts) + (job.session_duration_minutes || 0) * 60000 : null
      let first2
      if (starts && ends > nowMs) {
        const evening = uk(starts).hh >= 17
        first2 = daysBetween(nowMs, starts) === 0
          ? `After ${evening ? 'tonight\'s' : 'today\'s'} session, paste the Vimeo link, the chapters and the notes.`
          : `After the session on ${dayWithName(starts, nowMs)}, paste the Vimeo link, the chapters and the notes.`
      } else {
        first2 = `The session ran ${starts ? relativeDay(starts, nowMs) : 'already'}. Paste the Vimeo link, the chapters and the notes.`
      }
      // changed: the board's "Cohort 3 gets an email when it goes live." (no new-recording email; the bell).
      // The bell is claimed only when hub.bell_will_ring says switching it on would ring (Adam, 5 Oct,
      // stage 6's batch): never for 1 that's been live before, or a product that doesn't speak yet
      const bell = ctx.bells?.[bellKey(job)] === true
      card.detail = `${first2} Everyone with ${productIn(job)} sees it as soon as it's switched on${bell ? ', with a note in the bell' : ''}.`
      // Recordings opens on the recording, or on the session still waiting for 1
      card.link = {
        label: 'Open recordings',
        to: job.recording_id ? `/admin/recordings?recording=${job.recording_id}` : job.session_id ? `/admin/recordings?session=${job.session_id}` : '/admin/recordings',
      }
      break
    }
    // a mastermind group's register (mastermindJobs.js) says how many of the group are still to mark, and stays
    // until everyone is
    case 'register_missing': {
      const when = job.session_starts_at ? dayWithName(job.session_starts_at, nowMs) : ''
      const label = job.session_module_label
        ? `${productTitle(job)}, ${job.session_module_label}`
        : (job.cohort_name || (job.mm_register && !job.session_title ? EVERY_GROUP : job.session_title) || productTitle(job))
      card.title = [label, when].filter(Boolean).join(', ')
      card.detail = job.mm_register
        ? stillToMark(job.mm_register)
        : 'No register yet. Mark who came from the list of who was expected.'
      card.link = job.session_id ? { label: 'Take the register', to: `/admin/recordings/dates/${job.session_id}` } : null
      break
    }
    // a group's reviews after its session (undrawn), opening Masterminds on the first due by name
    case 'mm_review': {
      const m = job.mm || {}
      const when = job.session_starts_at ? dayWithName(job.session_starts_at, nowMs) : ''
      card.title = [job.cohort_name || productTitle(job), when].filter(Boolean).join(', ')
      card.detail = `${m.left} of ${m.total} still to review.`
      card.link = m.first && job.product_slug
        ? { label: `Review, starting with ${m.first.full_name}`, to: adminPath.mmMember(job.product_slug, m.first.person_id, `group=${job.cohort_id || 'none'}&show=review`) }
        : null
      break
    }
    // a member still to set up (undrawn): only the first gap, then the next once that's done
    case 'mm_setup': {
      card.title = nameProduct
      card.detail = setupWords(job.mm)
      card.link = member
      break
    }
    case 'client_session_soon': {
      const s = job.client_session || {}
      const at = s.starts_at ? `${relativeDay(s.starts_at, nowMs)} at ${time(s.starts_at)}` : 'soon'
      card.title = `${full}, session ${s.number ?? ''} ${at}`.replace(/\s+/g, ' ')
      const parts = []
      if (s.previous_number && s.actions_total) parts.push(`${s.actions_done ?? 0} of ${s.actions_total} actions ticked since session ${s.previous_number}.`)
      else if (s.previous_number) parts.push(`No actions were agreed at session ${s.previous_number}.`)
      if (s.files_from_client) parts.push(`${s.files_from_client === 1 ? '1 new file' : `${s.files_from_client} new files`} from ${first} ${s.files_from_client === 1 ? 'is' : 'are'} in your shared files.`)
      if (s.has_zoom_link === false) parts.push('There\'s no Zoom link on it yet.')
      card.detail = parts.join(' ')
      card.link = job.client_space_id ? { label: `Open ${possessive(first)} space`, to: spaceLink(job.product_path, job.client_space_id) } : person
      break
    }
    case 'email_send_failed': {
      const what = TEMPLATES[d.template] || 'An email'
      card.title = job.person_id ? `${what} to ${full} didn't send` : `${what} didn't send`
      card.detail = d.template === 'login_code' || d.template === 'new_email_code'
        ? 'Resend didn\'t take it, even after trying again. Codes are never kept, so the next request sends a fresh code.'
        : `Resend didn't take it on ${on(job.raised_at)}, even after trying again. Nothing else changed.${job.person_id && ['access', 'resend_login'].includes(d.template) ? ` Resend login email on ${possessive(first)} page tells ${first} how to log in.` : ''}`
      card.link = person
      break
    }
    case 'data_request': {
      const r = job.data_request || {}
      const kindOf = r.kind || d.request_kind
      const asked = r.requested_at || job.raised_at
      if (kindOf === 'delete') {
        card.title = `${full} asked to be deleted`
        card.detail = `Asked on ${on(asked)} at ${time(asked)}. Email ${first} to confirm first, as Account promised. Deleting wipes ${possessive(first)} details and ends access to everything, and can't be undone.`
        if (ctx.role === 'admin') card.note = 'Only an owner can delete someone or decline this.'
        act('delete', `Delete ${first}`, OWNER)
        act('decline', 'Decline', OWNER, 'line')
      } else {
        card.title = `${full} asked for a copy of the data the hub holds`
        const by = Date.parse(asked) + DAY_MS
        // Account's promise as it now reads (Adam, 5 Oct, question 7)
        card.detail = `Asked on ${on(asked)} at ${time(asked)}. Account said the copy would be ready within a day, so by ${time(by)} on ${on(by)}.`
        act('export', 'Make the export')
        act('decline', 'Decline', OA, 'line')
      }
      card.link = person
      break
    }
    // the Accelerator's term: access follows the subscription, so nothing changes on its own (undrawn)
    case 'term_final_month':
    case 'term_past_end': {
      card.title = nameProduct
      // the next step, in Masterminds (the Accelerator critique's admin 1, 20 Oct)
      const months = d.term_months || 12
      card.detail = `${termJobDetail(job)} ${kind === 'term_final_month'
        ? `Check ${possessive(first)} ${months}-month record before the term ends.`
        : `Check ${possessive(first)} ${months}-month record.`}`
      card.link = member
      break
    }
    // a member asked for the deal surgery on Profit On Your Terms; the hub sends nothing, so Adam sends a
    // time himself and ticks it off (undrawn: database builder 2's line, with the name for "their")
    case 'deal_surgery': {
      card.title = nameProduct
      card.detail = `${first} asked for a deal surgery.${job.cohort_name ? ` ${job.cohort_name}.` : ''}`
      card.link = person
      break
    }
    // a Stripe buyer of a product that releases by intake (The Funding Engine) went into an intake that had
    // already started, or into none (migration 20261010233000): Adam checks it, and puts it right with Change
    // cohort on their page (undrawn). The intake they went into is the flag's; the 1 they're in now is the row's.
    case 'intake_check': {
      const place = d.place === true
      const secured = job.access_status === 'secured'
      card.title = secured ? `${nameProduct}, place secured` : nameProduct
      const bought = place
        ? `${first} secured a place on ${productIn(job)} on ${on(job.raised_at)}`
        : `${first} bought ${productIn(job)} on ${on(job.raised_at)}`
      const sees = what => (place && secured ? `Once the place is paid, ${first} sees ${what}.` : `${first} sees ${what}.`)
      card.detail = d.reason === 'no_intake'
        ? `${bought}, with no intake running or to come, so ${first} isn't in one. ${sees('every week any intake has had')} Put ${first} in an intake with Change cohort on ${possessive(first)} page.`
        : `${bought} and went into ${d.intake || 'an intake'}, which had already started. ${sees('the weeks it has had')} If ${first} is meant for a later intake, change it with Change cohort on ${possessive(first)} page.`
      if (job.cohort_name && job.cohort_name !== d.intake) card.detail += ` ${first} is now in ${job.cohort_name}.`
      card.link = person
      break
    }
    case 'introduction': {
      Object.assign(card, introductionCard(job, ctx))
      card.actions = card.actions.map(a => ({ roles: OA, kind: 'ink', ...a }))
      break
    }
    case 'login_email_mismatch': {
      card.title = `${possessive(full)} login email doesn't match`
      const why = LOGIN_REASONS[d.reason]
      const fix = LOGIN_OWN_FIX.has(d.reason) ? '' : ` Change the login email on ${possessive(first)} page to put it right.`
      card.detail = `${why ? why(first) : 'The login and the hub disagree about the email, so no login code went.'}${fix}`
      card.link = person
      break
    }
    default:
      card.title = job.person_id ? nameProduct : productTitle(job) || 'A job'
      card.link = person
  }
  return card
}

// The ids of the introductions Today's referral cards name (each line, and a second claim's first claim), for
// hub.staff_introductions({ ids }) once the jobs are in (owner and admin only)
export function introductionIds(jobs = []) {
  const out = new Set()
  for (const j of jobs) {
    if (j.kind !== 'introduction') continue
    const d = j.detail || {}
    if (d.introduction_id) out.add(d.introduction_id)
    if (d.first_introduction_id) out.add(d.first_introduction_id)
  }
  return [...out].sort()
}

// The introductions a full refund or a dispute has touched, from their own cards on Today (reason refunded): line
// id to the first such card's day, so the line's owed card says Refunded in place of Mark paid (the review of 18 Oct)
export function refundedLines(jobs = []) {
  const out = new Map()
  for (const j of jobs) {
    const d = j.detail || {}
    if (j.kind !== 'introduction' || d.reason !== 'refunded' || !d.introduction_id || !j.raised_at) continue
    const was = out.get(d.introduction_id)
    if (!was || Date.parse(j.raised_at) < Date.parse(was)) out.set(d.introduction_id, j.raised_at)
  }
  return out
}

// A referral card (docs/referrals-spec.md 4.9), by the flag's reason. ctx.introductions: the lines by id, from
// hub.staff_introductions; until they're read, the card shows without the names and the amount.
export function introductionCard(job, ctx = {}) {
  const nowMs = ctx.nowMs ?? Date.now()
  const d = job.detail || {}
  const reason = d.reason
  const line = ctx.introductions?.get?.(d.introduction_id) || null
  const firstClaim = ctx.introductions?.get?.(d.first_introduction_id) || null
  const on = t => dayShort(t, nowMs)
  const first = realFirstName(line?.referrer_first_name) || firstOf(job)
  const referred = line?.referred_name || 'Someone'
  const product = productIn(line ? { ...job, product_name: line.product_name || job.product_name } : job)
  const day = on(line?.introduced_at || job.raised_at)
  const verb = (line?.source || (job.source === 'system' ? 'tally' : 'stripe')) === 'tally' ? `applied for ${product}` : `paid for ${product}`
  const through = `${referred} ${verb} through ${possessive(first)} link on ${day}`
  const lineId = d.introduction_id || null
  const out = {
    title: `${fullOf(job)}, ${productTitle(job)}`,
    detail: '',
    link: { label: 'Open referrals', to: lineId ? `/admin/referrals?line=${lineId}` : '/admin/referrals' },
    actions: [],
  }
  const decide = () => out.actions.push({ op: 'intro_accept', label: 'Accept' }, { op: 'intro_turn_down', label: 'Turn down', kind: 'line' })
  switch (reason) {
    case 'new': {
      const as = REF_STATUS[line?.status] || (verb.startsWith('applied') ? 'Applied' : 'Joined')
      out.detail = `${through}. It's on ${possessive(first)} referrals as ${as}.`
      if ((line?.source || 'stripe') === 'stripe' && !verb.startsWith('applied')) out.detail += ' Qualify it once a refund can no longer come in.'
      break
    }
    case 'owed': {
      const amount = line?.payout_amount != null ? poundsWord(line.payout_amount) : 'The fee'
      const opening = job.source === 'stripe_event'
        ? `${through}.`
        : `${possessive(first)} referral of ${referred} qualified on ${on(line?.qualified_at || job.raised_at)}.`
      // a refund since (its own card, ctx.refunds; the review of 18 Oct): the card says so in place of Mark paid
      const refundedAt = ctx.refunds?.get?.(d.introduction_id) || null
      if (refundedAt && (!line || line.status === 'qualified')) {
        out.detail = `${opening} ${amount} is owed to ${first} on the ledger.`
        out.note = `Refunded ${on(refundedAt)}: mark it Not proceeding on Referrals if it shouldn't be paid.`
        break
      }
      out.detail = `${opening} ${amount} is owed to ${first}. Pay it by bank transfer, then mark it paid.`
      if (!line || line.status === 'qualified') out.actions.push({ op: 'intro_paid', label: 'Mark paid' })
      break
    }
    case 'not_ticked':
      out.detail = `${through}, but ${first} isn't ticked to refer ${product}. Accept it to count it, or turn it down.`
      decide()
      break
    case 'self_referral':
      out.detail = `${first} ${verb} through ${possessive(first)} own link on ${day}. Accept it to count it, or turn it down.`
      decide()
      break
    case 'second_claim': {
      const other = line?.first_referrer_full_name || firstClaim?.referrer_full_name || 'someone else'
      const firstDay = firstClaim?.introduced_at ? `, on ${on(firstClaim.introduced_at)}` : ''
      out.detail = `${through}, but ${other} introduced them first${firstDay}. Accept it to count both, or turn it down.`
      decide()
      break
    }
    case 'refunded': {
      const what = d.refund_kind === 'dispute' ? 'disputed' : 'refunded'
      const status = line ? statusInSentence(line.status) : ''
      out.detail = `${possessive(referred)} payment for ${product}, through ${possessive(first)} link, was ${what} on ${on(job.raised_at)}.`
        + (status ? ` The introduction is ${status}.` : '')
        + ' Nothing changes on its own: mark it Not proceeding if it shouldn\'t be paid.'
      if (line?.status === 'paid') out.detail += ` It was paid to ${first} on ${on(line.paid_at)}.`
      if (line && !['paid', 'not_proceeding'].includes(line.status)) out.actions.push({ op: 'intro_not_proceeding', label: 'Not proceeding', kind: 'line' })
      break
    }
    // the code the link carried, when the card has it (raised since the review of 13 Oct), is the clue to who
    // introduced them, so the card names it
    case 'unknown_code': {
      const code = d.code ? `the code ${d.code}, which nobody in the hub holds` : 'a code nobody in the hub holds'
      out.title = `${productTitle(job) || 'A product'}, a link nobody holds`
      out.detail = `${job.person_full_name || 'Someone'} paid for ${product} on ${on(job.raised_at)} through a referral link with ${code}, so it isn't credited to anyone. If you know who introduced them, add it by hand on Referrals.`
      break
    }
    case 'not_recorded': {
      const link = d.code ? `a referral link with the code ${d.code}` : 'a referral link'
      out.title = `${productTitle(job) || 'A sale'}, a referral not recorded`
      out.detail = job.person_full_name
        ? `${job.person_full_name} paid for ${product} through ${link} on ${on(job.raised_at)}, but it couldn't be recorded, so it isn't credited to anyone. Add it by hand on Referrals.`
        : `A sale of ${product} through ${link} on ${on(job.raised_at)} couldn't be recorded, so it isn't credited to anyone. Add it by hand on Referrals.`
      break
    }
    case 'refund_not_checked':
      out.title = 'A refund not checked against referrals'
      out.detail = `A refund from Stripe on ${on(job.raised_at)} couldn't be checked against referrals. Look at it on Referrals.`
      break
    default:
      out.detail = lineId ? `${through}.` : ''
  }
  return out
}

// A session for every group, as a register's title names it when its row has no title
const EVERY_GROUP = 'Every group'
// "2 of 5 still to mark."
export const stillToMark = ({ left, total }) => `${left} of ${total} still to mark.`
// A member's first set-up gap (mastermindJobs.js setupGap)
export function setupWords(g = {}) {
  if (g.gap === 'no_group') return 'No group yet.'
  if (g.gap === 'no_actions') return 'No month 1 actions yet.'
  if (g.gap === 'goal') {
    return g.gone <= g.days
      ? `Goal to agree, ${g.gone} of ${g.days} days gone.`
      : `Goal to agree, ${g.gone} days gone, ${g.days} allowed.`
  }
  return ''
}

// 3 hours 20 minutes, 45 minutes
export function spanWords(seconds) {
  const m = Math.round((seconds || 0) / 60)
  const h = Math.floor(m / 60), r = m % 60
  const p = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`
  if (!h) return p(r, 'minute')
  return r ? `${p(h, 'hour')} ${p(r, 'minute')}` : p(h, 'hour')
}

// The line under a job once its button has done its work (the board's bold line), from the answer
export function doneLine(op, job, answer, extra = {}) {
  const first = firstOf(job)
  switch (op) {
    case 'card_link': return job.person_email ? `Card update link sent to ${job.person_email}` : (answer?.message || 'Card link sent.')
    case 'reply': return answer?.emailed && job.person_email ? `Reply sent to ${job.person_email}.` : (answer?.message || 'Reply sent.')
    case 'messaged': return `Marked as messaged. It's on ${possessive(first)} history.`
    case 'keep_access': return `Access kept. It's on ${possessive(first)} history.`
    case 'give_7_days': return extra.due ? `Done. The balance is now due by ${time(extra.due)} on ${dayShort(extra.due, extra.nowMs)}.` : 'Done.'
    case 'add_reason': return reasonDone(extra.reason)
    case 'revoke': return revokeDone(job)
    case 'handled': return MISMATCH_WORDS.handledDone
    case 'intro_accept': return 'Accepted.'
    case 'intro_turn_down': return 'Turned down.'
    case 'intro_not_proceeding': return 'Marked not proceeding.'
    case 'intro_paid': return 'Marked paid.'
    case 'revoke_certificate': return `Certificate ${extra.number || ''} revoked. It's on ${possessive(first)} history.`.replace('  ', ' ')
    default: return answer?.message || 'Done.'
  }
}

// What a ticked job says beside it: the database's resolution, and who ticked it at what time (the same
// line the Price map shows, src/admin/mismatches.js)
export { doneStamp }

// The line top right of the page: the board's "3 of 8 things need you"
export function leftLine(jobs) {
  const left = jobs.filter(j => j.status === 'open').length
  if (left === 0) return 'Nothing left for today'
  return `${left} of ${jobs.length} ${jobs.length === 1 ? 'thing' : 'things'} ${left === 1 ? 'needs' : 'need'} you`
}

// The eyebrow: WEDNESDAY 30 SEPTEMBER (the styles put it in capitals)
export const eyebrowFor = nowMs => longDate(nowMs)

// ---- Live today and Coming up --------------------------------------------------------------------

const isFreeWebinar = s => s.kind === 'webinar'
// A schedule row's 2 lines: the board's "The Funding Engine, Week 5" / "Cohort 3, 7pm"
export function scheduleLines(s) {
  const at = time(s.starts_at)
  if (s.kind === 'client_session') return { title: `1-2-1, ${s.person_name}`, detail: `Session ${s.session_number}, ${at}` }
  if (isFreeWebinar(s)) {
    return { title: 'Free webinar', detail: `${s.title}, ${at}` }
  }
  const product = productTitle(s)
  if (s.module_label) return { title: `${product}, ${s.module_label}`, detail: `${[s.cohort_name, at].filter(Boolean).join(', ')}` }
  return { title: s.cohort_name || s.title || product, detail: `${product}, ${at}` }
}
export const dateBlockOf = s => dateBlock(s.starts_at)

// The black Live today card for 1 item today. A mastermind group's session (mastermind: true) counts its register
// from the group's members (register: { left, total }, mastermindJobs.js), when it's been read, and opens the
// group's sessions on Cohorts and dates
export function liveLines(s, nowMs = Date.now(), { mastermind = false, register = null } = {}) {
  const at = time(s.starts_at)
  const ended = Date.parse(s.starts_at) + (s.duration_minutes || 0) * 60000 <= nowMs
  if (s.kind === 'client_session') {
    return {
      title: `1-2-1, ${s.person_name}, session ${s.session_number}, ${at}`,
      detail: s.has_zoom_link ? 'Zoom link set.' : 'No Zoom link yet.',
      link: s.space_id ? { label: `Open ${possessive(String(s.person_name || '').split(' ')[0] || 'the client')} space`, to: spaceLink(s.product_path, s.space_id) } : null,
    }
  }
  if (isFreeWebinar(s)) {
    return {
      title: `Free webinar, ${s.title}, ${at}`,
      // has_zoom_link: its Zoom registration link, typed in Webinars (stage 6)
      detail: s.has_zoom_link === false ? 'No registration link yet.' : 'Register is open on Zoom\'s own page.',
      link: { label: 'Open webinars', to: `/admin/webinars/${s.item_id}` },
    }
  }
  const lines = scheduleLines(s)
  const parts = []
  if (s.cohort_name) parts.push(`${s.cohort_name}.`)
  parts.push(s.has_zoom_link ? 'Zoom link set.' : 'No Zoom link yet.')
  const title = s.module_label ? `${lines.title}, ${at}` : `${lines.title}, ${productTitle(s)}, ${at}`
  const takeIt = { label: 'Take the register', to: `/admin/recordings/dates/${s.item_id}` }
  if (ended && register) {
    if (register.left) {
      parts.push(`It's finished. ${stillToMark(register)}`)
      return { title, detail: parts.join(' '), link: takeIt }
    }
  } else if (ended && !s.register_rows) {
    parts.push('It\'s finished, and the register isn\'t taken yet.')
    return { title, detail: parts.join(' '), link: takeIt }
  }
  if (mastermind && s.cohort_id && s.cohort_name) {
    return { title, detail: parts.join(' '), link: { label: `Open ${possessive(s.cohort_name)} sessions`, to: `${adminPath.dates()}#cohort-${s.cohort_id}` } }
  }
  return { title, detail: parts.join(' '), link: { label: 'Open the cohort', to: '/admin/recordings/dates' } }
}

// ---- New this week, Going quiet, Access ending --------------------------------------------------

export function freshWhat(f, amounts, products = []) {
  const name = productTitle(typed(f, products))
  if (f.status === 'secured') return `${name}, place secured`
  if (f.source === 'manual') return `${name}, paid another way`
  const amount = amountOf(amounts, { checkout: f.checkout_session_id, price: f.price_id })
  return amount ? `${name}, ${amount}` : name
}

// Where a row opens: a mastermind's opens the member view in Masterminds for owner and admin
// (docs/mastermind-admin-plan.md 3.4); any other, and for support and a host, the person's page
export function memberHref(row, products = [], role = 'owner') {
  const p = (products || []).find(x => x.id === row.product_id)
  return p?.type === 'mastermind' && p.moved_in && (role === 'owner' || role === 'admin')
    ? `/admin/masterminds/${p.slug}/${row.person_id}` : `/admin/people/${row.person_id}`
}

export function quietWhy(q, nowMs = Date.now()) {
  const reasons = q.reasons || []
  const parts = []
  if (reasons.includes('no_login')) {
    parts.push(q.last_active_at ? `Last logged in ${q.days_quiet} ${q.days_quiet === 1 ? 'day' : 'days'} ago` : `Not logged in yet, ${q.days_quiet} days since access began`)
  }
  if (reasons.includes('no_ticks')) {
    parts.push(q.last_tick_at ? `nothing ticked since ${dayShort(q.last_tick_at, nowMs)}` : 'nothing ticked yet')
  }
  const line = parts.join(', and ')
  return `${q.product_name}. ${line.charAt(0).toUpperCase()}${line.slice(1)}.`
}

export function endingWhy(e, nowMs = Date.now(), products = []) {
  const ends = `${productTitle(typed(e, products))}. Ends ${dayWithName(e.ends_at, nowMs)}.`
  if (e.promised_until && e.promised_until > ukDayString(e.ends_at)) {
    return `${ends} The terms promise it until ${dayShort(e.promised_until, nowMs)}.`
  }
  return ends
}
function ukDayString(t) {
  const u = uk(t)
  return `${u.y}-${String(u.m).padStart(2, '0')}-${String(u.d).padStart(2, '0')}`
}

// ---- Announcements --------------------------------------------------------------------------------

// Who an announcement is for, as staff choose it and see it. Everyone reaches free accounts too (a register
// page's, decision 57), so it says so (Adam, 23 Oct).
export const AUDIENCE_WORDS = {
  everyone: 'Everyone, free accounts included',
  product_owners: 'Owners of a product',
}

// The day it comes down: the last day it shows (it goes at the start of the next)
export function comesDownDay(t) {
  return t ? Date.parse(t) - 1000 : null
}
export function announcementMeta(a, products = [], nowMs = Date.now()) {
  const who = a.audience === 'product_owners'
    ? `Owners of ${productIn(products.find(p => p.id === a.product_id) || null)}`
    : AUDIENCE_WORDS.everyone
  const posted = daysBetween(nowMs, a.starts_at) === 0 ? 'today' : dayWithName(a.starts_at, nowMs)
  const down = a.comes_down_at ? `comes down ${dayWithName(comesDownDay(a.comes_down_at), nowMs)}` : 'stays until you take it down'
  return `${who}. Posted ${posted}, ${down}`
}

// A hub page an announcement's button can open: the free pages, Account and each product live in the hub
export function buttonPages(products = []) {
  const pages = [
    { value: '/free-webinars', label: 'Free webinars' },
    { value: '/free-resources', label: 'Free resources' },
    { value: '/account', label: 'Your account' },
  ]
  for (const p of products) {
    if (!p.path || !p.moved_in || p.state !== 'live' || p.access_kind !== 'paid' || p.type === 'client_space') continue
    pages.push({ value: p.path, label: productTitle({ ...p, product_name: p.name, product_type: p.type, product_slug: p.slug }) })
  }
  return pages
}
